import { personLedger } from '../services/financialLedger';
import { toCents, fromCents } from '../services/financialMath';
import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../contexts/AppContext';
import { format } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiUsers, FiUser, FiMail, FiPhone, FiDollarSign, FiClock, FiEye } = FiIcons;

const PEOPLE_FILTERS = [
  {key:'all',label:'All'},
  {key:'you-owe',label:'You Owe'},
  {key:'owes-you',label:'Owes You'},
  {key:'recent',label:'Recent Activity'},
  {key:'email',label:'Has Email'},
  {key:'phone',label:'Has Phone'}
];

const PeopleSummaryCard = ({Icon, label, value, accent='text-slate-900', tint='bg-blue-50 text-blue-600'}) => (
  <div className="flex min-w-0 items-center gap-3 sm:gap-4 rounded-2xl border border-[#dfe7f0] bg-white px-4 py-4 sm:px-5 shadow-[0_3px_12px_rgba(15,43,82,0.04)]">
    <span className={'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl '+tint}><Icon size={23}/></span>
    <div className="min-w-0">
      <p className="text-xs font-semibold text-slate-600">{label}</p>
      <p className={'mt-1 truncate text-xl sm:text-2xl font-extrabold tabular-nums '+accent} title={String(value)}>{value}</p>
    </div>
  </div>
);

const People = () => {
  const { state, actions } = useApp();
  const location=useLocation(),navigate=useNavigate();
  const { people, transactions, ious, settings } = state;
  const [showAddForm, setShowAddForm] = useState(false);
  const [search, setSearch] = useState(()=>new URLSearchParams(location.search).get('search')||'');
  useEffect(()=>setSearch(new URLSearchParams(location.search).get('search')||''),[location.search]);
  const updateSearch=value=>{
    setSearch(value);
    const params=new URLSearchParams(location.search);
    if(value.trim())params.set('search',value);
    else params.delete('search');
    navigate({pathname:'/people',search:params.toString()?'?'+params.toString():''},{replace:true});
  };
  const [activeFilter, setActiveFilter] = useState('all');

  const peopleWithBalances = useMemo(() => people.map(person => {
    const {currentBalance,ledgerEntries} = personLedger(person, transactions, ious);
    const latest = ledgerEntries[ledgerEntries.length - 1];
    return {...person,balance:currentBalance,
      lastActivity:latest ? new Date(latest.date + 'T12:00:00') : null,
      transactionCount:ledgerEntries.length};
  }), [people,transactions,ious]);

  // Use the existing ledger's signed balances: positive = owed to the user.
  // Perform summary arithmetic in cents; nothing is persisted or synced.
  const summary = useMemo(() => peopleWithBalances.reduce((totals, person) => {
    const cents = toCents(person.balance);
    if(cents > 0)totals.owedToYou += cents;
    if(cents < 0)totals.youOwe += -cents;
    return totals;
  },{youOwe:0,owedToYou:0}),[peopleWithBalances]);

  const visiblePeople = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matching = peopleWithBalances.filter(person => {
      const contact = String(person.contact||'');
      const haystack = [person.name,person.shortName,contact,person.notes].join(' ').toLowerCase();
      if(term && !haystack.includes(term))return false;
      if(activeFilter === 'you-owe')return person.balance < 0;
      if(activeFilter === 'owes-you')return person.balance > 0;
      if(activeFilter === 'email')return contact.includes('@');
      if(activeFilter === 'phone')return Boolean(contact) && !contact.includes('@');
      return true;
    });
    return activeFilter === 'recent'
      ? matching.slice().sort((a,b)=>(b.lastActivity?.getTime()||0)-(a.lastActivity?.getTime()||0))
      : matching;
  },[peopleWithBalances,search,activeFilter]);

  const currency = amount => new Intl.NumberFormat('en-AU',{
    style:'currency',currency:settings.currency||'AUD'
  }).format(amount);

  const animationProps = settings.reducedMotion ? {} : {
    initial:{opacity:0,y:12},animate:{opacity:1,y:0},transition:{duration:0.25}
  };

  return (
    <motion.div className="space-y-5 md:space-y-6 pb-6" {...animationProps}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-950">People</h1>
          <p className="mt-1 text-sm text-slate-600">Manage IOUs and shared expenses</p>
        </div>
        <button type="button" onClick={()=>setShowAddForm(true)}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#079de2] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#0176D3] focus-visible:ring-2 focus-visible:ring-blue-500">
          <FiPlus size={18}/> Add Person
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <PeopleSummaryCard Icon={FiIcons.FiUsers} label="Total People" value={String(peopleWithBalances.length)}/>
        <PeopleSummaryCard Icon={FiIcons.FiArrowUpRight} label="You Owe"
          value={currency(fromCents(summary.youOwe))} accent="text-rose-600" tint="bg-rose-50 text-rose-600"/>
        <PeopleSummaryCard Icon={FiIcons.FiArrowDownLeft} label="Owed to You"
          value={currency(fromCents(summary.owedToYou))} accent="text-emerald-700" tint="bg-emerald-50 text-emerald-600"/>
        <PeopleSummaryCard Icon={FiIcons.FiCompass} label="Net Position"
          value={currency(fromCents(summary.owedToYou-summary.youOwe))}
          accent={summary.owedToYou-summary.youOwe<0?'text-rose-600':'text-emerald-700'}/>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <label className="relative block w-full lg:max-w-[420px]">
          <span className="sr-only">Search people</span>
          <FiIcons.FiSearch aria-hidden="true" size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"/>
          <input type="search" value={search} onChange={event=>updateSearch(event.target.value)}
            placeholder="Search people..." aria-label="Search people"
            className="min-h-11 w-full rounded-2xl border border-[#d9e4f1] bg-[#f7f9fc] py-3 pl-11 pr-4 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"/>
        </label>
        <div className="flex flex-wrap gap-2" aria-label="Filter people">
          {PEOPLE_FILTERS.map(filter=>(
            <button key={filter.key} type="button" onClick={()=>setActiveFilter(filter.key)}
              aria-pressed={activeFilter===filter.key}
              className={'min-h-10 rounded-full border px-3.5 py-2 text-xs sm:text-sm font-semibold transition focus-visible:ring-2 focus-visible:ring-blue-400 '+(
                activeFilter===filter.key
                  ? 'border-[#009dec] bg-[#079de2] text-white shadow-sm'
                  : 'border-[#d9e4f1] bg-white text-[#304663] hover:border-blue-200 hover:bg-blue-50'
              )}>{filter.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {visiblePeople.map(person=>{
          const balanceCents = toCents(person.balance);
          const owesYou = balanceCents>0;
          const youOwe = balanceCents<0;
          const label = owesYou?'Owes you':youOwe?'You owe':'Settled';
          const contact = String(person.contact||'');
          return (
            <motion.article key={person.id} {...animationProps}
              className="flex min-w-0 flex-col rounded-2xl border border-[#dce5f0] bg-white p-4 sm:p-5 shadow-[0_3px_10px_rgba(15,43,82,0.045)] transition-shadow hover:shadow-md">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600"><FiUser size={23}/></span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="truncate text-base font-extrabold text-slate-950">{person.name}</h2>
                    <span className={'rounded-full px-2.5 py-1 text-[11px] font-bold '+(
                      owesYou?'bg-emerald-100 text-emerald-800':youOwe?'bg-rose-100 text-rose-700':'bg-slate-100 text-slate-600'
                    )}>{label}</span>
                  </div>
                  {person.shortName && person.shortName!==person.name && (
                    <p className="mt-1 text-sm text-slate-600">({person.shortName})</p>
                  )}
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm text-slate-600">
                {contact && <div className="flex min-w-0 items-center gap-2">
                  {contact.includes('@')?<FiMail className="shrink-0"/>:<FiPhone className="shrink-0"/>}
                  <span className="truncate" title={contact}>{contact}</span>
                </div>}
                <div className="flex items-center gap-2">
                  <FiClock className="shrink-0"/>
                  <span>Last activity: {person.lastActivity ? format(person.lastActivity,'MMM d, yyyy') : 'No activity yet'}</span>
                </div>
              </div>
              <div className={'mt-4 rounded-xl px-4 py-3 '+(
                owesYou?'bg-emerald-50':youOwe?'bg-rose-50':'bg-slate-100'
              )}>
                <div className="flex items-center justify-between text-xs font-semibold text-slate-600">
                  <span>Balance</span><FiDollarSign size={16} className={owesYou?'text-emerald-600':youOwe?'text-rose-600':'text-slate-500'}/>
                </div>
                <p className={'mt-1 text-lg font-extrabold tabular-nums '+(
                  owesYou?'text-emerald-700':youOwe?'text-rose-700':'text-slate-700'
                )}>{label} {currency(Math.abs(person.balance))}</p>
              </div>
              <p className="mt-3 min-h-[1.4rem] break-words text-sm italic text-slate-600">
                {person.notes ? '"' + person.notes + '"' : '\u00a0'}
              </p>
              <div className="mt-auto flex gap-2 pt-3">
                <Link to={'/people/'+person.id} state={{openAddIOU:true}}
                  aria-label={'Add IOU for '+person.name}
                  className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-[#e0f2fe] px-3 py-2 text-sm font-bold text-[#0369a1] transition hover:bg-[#c7eaff] focus-visible:ring-2 focus-visible:ring-blue-500">
                  <FiPlus size={16}/> Add IOU
                </Link>
                <Link to={'/people/'+person.id} aria-label={'View '+person.name}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#f1f4f8] px-3 py-2 text-sm font-semibold text-[#344560] transition hover:bg-slate-200 focus-visible:ring-2 focus-visible:ring-blue-500">
                  <FiEye size={17}/> <span className="hidden sm:inline">View</span>
                </Link>
              </div>
            </motion.article>
          );
        })}
        {!visiblePeople.length&&<div className="col-span-full rounded-2xl border border-[#dce5f0] bg-white p-10 text-center">
          <FiUsers size={39} className="mx-auto mb-3 text-slate-400"/>
          <h3 className="text-lg font-bold text-slate-900">{people.length?'No people match your filters':'No people yet'}</h3>
          <p className="mt-2 text-sm text-slate-500">{people.length?'Try a different search or filter.':'Add people to track IOUs and shared expenses.'}</p>
          {people.length===0&&<button type="button" onClick={()=>setShowAddForm(true)}
            className="mt-5 rounded-xl bg-[#0176D3] px-4 py-3 text-sm font-bold text-white">Add Your First Person</button>}
        </div>}
      </div>

      {showAddForm&&<AddPersonForm onClose={()=>setShowAddForm(false)} onSave={async person=>{
        try {
          await actions.addPerson(person);
          setShowAddForm(false);
        } catch(error) {
          window.alert(error.message||'Failed to save person');
        }
      }}/>}
    </motion.div>
  );
};

// Add Person Form Component
const AddPersonForm = ({ onClose, onSave }) => {
  const { state } = useApp();
  const { settings } = state;
  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    contact: '',
    notes: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      return;
    }

    const person = {
      ...formData,
      id: Date.now(),
      balance: 0,
      createdAt: new Date(),
    };

    onSave(person);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const animationProps = settings.reducedMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
        transition: { duration: 0.3 },
      };

  return (
    <motion.div
      className="mobile-full-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      {...animationProps}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Add Person</h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Full name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Short Name
            </label>
            <input
              type="text"
              value={formData.shortName}
              onChange={(e) => handleChange('shortName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Nickname or short name (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact
            </label>
            <input
              type="text"
              value={formData.contact}
              onChange={(e) => handleChange('contact', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Email or phone number (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Optional notes about this person..."
            />
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!formData.name}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors duration-200"
          >
            Add Person
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default People;